import { ErrorMessage, Field, useFormikContext } from "formik";
import React from "react";
import { toast } from "react-toastify";
import { FileUpload } from "../../../registration/helper/FileUpload";
import { uploadSchemeReport } from "../../api/SchemeDataEntry.api";

type Props = {
	isView?: boolean;
};

const caseStudyOptions = [
	"Para",
	"Positive Aspect",
	"Innovative practice",
	"Audit process",
	"impact of the audit",
	"others",
];

const NMPStep6: React.FC<Props> = ({ isView = false }) => {
	const { values, setFieldValue } = useFormikContext<any>();

	return (
		<div className="w-100">
			{/* Audit Observations & Remarks */}
			<div className="card shadow-none border p-5 mb-8 rounded bg-light-subtle">
				<h3 className="card-title fw-bold fs-3 mb-5 text-dark">
					Audit Observations & Remarks
				</h3>
				<div className="row g-9 mb-5">
					<div className="col-md-12 fv-row">
						<label className="form-label required fs-5 fw-bold text-dark">
							Unique Aspects Observed During the Social Audit
						</label>
						<Field
							as="textarea"
							name="unique_aspects"
							className="form-control form-control-solid"
							rows={3}
							disabled={isView}
							placeholder="Enter Unique Aspects"
						/>
						<div className="text-danger mt-1 fw-bold fs-7">
							<ErrorMessage name="unique_aspects" />
						</div>
					</div>
					<div className="col-md-12 fv-row">
						<label className="form-label required fs-5 fw-bold text-dark">
							Village / School Committee Remarks (Key Resolutions)
						</label>
						<Field
							as="textarea"
							name="committee_remarks"
							className="form-control form-control-solid"
							rows={3}
							disabled={isView}
							placeholder="Enter Remarks"
						/>
						<div className="text-danger mt-1 fw-bold fs-7">
							<ErrorMessage name="committee_remarks" />
						</div>
					</div>
					<div className="col-md-12 fv-row">
						<label className="form-label required fs-5 fw-bold text-dark">
							Were There Any Changes in the School Due to the Audit?
						</label>
						<div className="d-flex align-items-center mt-3">
							<label className="form-check form-check-inline form-check-solid me-5">
								<Field
									className="form-check-input"
									name="changes_due_to_audit"
									type="radio"
									value="yes"
									disabled={isView}
								/>
								<span className="fw-bold ps-2 fs-6">Yes</span>
							</label>
							<label className="form-check form-check-inline form-check-solid">
								<Field
									className="form-check-input"
									name="changes_due_to_audit"
									type="radio"
									value="no"
									disabled={isView}
								/>
								<span className="fw-bold ps-2 fs-6">No</span>
							</label>
						</div>
						{values.changes_due_to_audit === "yes" && (
							<div className="mt-4">
								<label className="form-label required fs-5 fw-bold text-dark">
									Paragraph (remarks)
								</label>
								<Field
									as="textarea"
									name="changes_due_to_audit_paragraph"
									className="form-control form-control-solid"
									rows={3}
									disabled={isView}
									placeholder="Describe the changes made in the school due to the audit"
								/>
								<div className="text-danger mt-1 fw-bold fs-7">
									<ErrorMessage name="changes_due_to_audit_paragraph" />
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Case Studies (Case Study 1 & Case Study 2) */}
			<div className="card shadow-none border p-5 mb-8 rounded bg-light-subtle">
				<h3 className="card-title fw-bold fs-3 mb-5 text-dark">
					Case Studies
				</h3>

				{/* Row 1: Case Study 1 */}
				<div className="row g-9 mb-6 align-items-center">
					<div className="col-md-6 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Case Study 1 Type
						</label>
						<Field
							as="select"
							name="case_study_1_type"
							className="form-select form-select-solid"
							disabled={isView}
							onChange={(e: any) => {
								const val = e.target.value;
								setFieldValue("case_study_1_type", val);
								if (!val) {
									setFieldValue("case_study_1_file", null);
								}
							}}
						>
							<option value="">Select Case Study 1 Type</option>
							{caseStudyOptions.map((opt) => (
								<option key={opt} value={opt}>
									{opt}
								</option>
							))}
						</Field>
					</div>

					<div className="col-md-6 fv-row">
						{values.case_study_1_type && (
							<FileUpload
								label="Case Study 1 File (PDF / DOCX)"
								value={values.case_study_1_file}
								disabled={isView}
								onChange={(val) => setFieldValue("case_study_1_file", val)}
								onUpload={async (file) => {
									const ext = file.name.toLowerCase();
									const isValidType =
										file.type === "application/pdf" ||
										file.type ===
											"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
										file.type === "application/msword" ||
										ext.endsWith(".pdf") ||
										ext.endsWith(".docx") ||
										ext.endsWith(".doc");

									if (!isValidType) {
										toast.error("Only PDF and DOCX format files are allowed!");
										throw new Error(
											"Only PDF and DOCX format files are allowed!",
										);
									}
									return await uploadSchemeReport(file, "nmp-case-study");
								}}
								accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								maxSizeMB={5}
								description="Max 5MB (PDF or DOCX format)"
							/>
						)}
					</div>
				</div>

				{/* Row 2: Case Study 2 */}
				<div className="row g-9 mb-4 align-items-center">
					<div className="col-md-6 fv-row">
						<label className="form-label fs-5 fw-bold text-dark">
							Case Study 2 Type
						</label>
						<Field
							as="select"
							name="case_study_2_type"
							className="form-select form-select-solid"
							disabled={isView}
							onChange={(e: any) => {
								const val = e.target.value;
								setFieldValue("case_study_2_type", val);
								if (!val) {
									setFieldValue("case_study_2_file", null);
								}
							}}
						>
							<option value="">Select Case Study 2 Type</option>
							{caseStudyOptions.map((opt) => (
								<option key={opt} value={opt}>
									{opt}
								</option>
							))}
						</Field>
					</div>

					<div className="col-md-6 fv-row">
						{values.case_study_2_type && (
							<FileUpload
								label="Case Study 2 File (PDF / DOCX)"
								value={values.case_study_2_file}
								disabled={isView}
								onChange={(val) => setFieldValue("case_study_2_file", val)}
								onUpload={async (file) => {
									const ext = file.name.toLowerCase();
									const isValidType =
										file.type === "application/pdf" ||
										file.type ===
											"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
										file.type === "application/msword" ||
										ext.endsWith(".pdf") ||
										ext.endsWith(".docx") ||
										ext.endsWith(".doc");

									if (!isValidType) {
										toast.error("Only PDF and DOCX format files are allowed!");
										throw new Error(
											"Only PDF and DOCX format files are allowed!",
										);
									}
									return await uploadSchemeReport(file, "nmp-case-study");
								}}
								accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								maxSizeMB={5}
								description="Max 5MB (PDF or DOCX format)"
							/>
						)}
					</div>
				</div>
			</div>

			{/* Audit Photos (Photo 1 Compulsory) */}
			<div className="card shadow-none border p-5 mb-8 rounded bg-light-subtle">
				<h3 className="card-title fw-bold fs-3 mb-5 text-dark">
					Audit Photos
				</h3>
				<div className="row g-9 mb-5">
					<div className="col-md-4 fv-row">
						<FileUpload
							label="Audit Photo 1"
							value={values.photo_1}
							disabled={isView}
							required={true}
							onChange={(val) => setFieldValue("photo_1", val)}
							onUpload={(file) => uploadSchemeReport(file, "nmp-audit-photo")}
							accept="image/*"
							maxSizeMB={2}
							description="Compulsory (Max 2MB, JPG/PNG)"
						/>
						<div className="text-danger mt-1 fw-bold fs-7">
							<ErrorMessage name="photo_1" />
						</div>
					</div>

					<div className="col-md-4 fv-row">
						<FileUpload
							label="Audit Photo 2"
							value={values.photo_2}
							disabled={isView}
							onChange={(val) => setFieldValue("photo_2", val)}
							onUpload={(file) => uploadSchemeReport(file, "nmp-audit-photo")}
							accept="image/*"
							maxSizeMB={2}
							description="Optional (Max 2MB, JPG/PNG)"
						/>
					</div>

					<div className="col-md-4 fv-row">
						<FileUpload
							label="Audit Photo 3"
							value={values.photo_3}
							disabled={isView}
							onChange={(val) => setFieldValue("photo_3", val)}
							onUpload={(file) => uploadSchemeReport(file, "nmp-audit-photo")}
							accept="image/*"
							maxSizeMB={2}
							description="Optional (Max 2MB, JPG/PNG)"
						/>
					</div>
				</div>
			</div>

			{/* Audit Report (PDF Only) */}
			<div className="card shadow-none border p-5 mb-5 rounded bg-light-subtle">
				<h3 className="card-title fw-bold fs-3 mb-5 text-dark">
					Audit Report
				</h3>
				<div className="row g-9 mb-5">
					<div className="col-md-12 fv-row">
						<FileUpload
							label="Audit Report (PDF format)"
							value={values.report_file}
							disabled={isView}
							onChange={(val) => setFieldValue("report_file", val)}
							onUpload={async (file) => {
								const ext = file.name.toLowerCase();
								if (
									file.type !== "application/pdf" &&
									!ext.endsWith(".pdf")
								) {
									toast.error("Only PDF format files are allowed!");
									throw new Error("Only PDF format files are allowed!");
								}
								return await uploadSchemeReport(file, "nmp-audit-report");
							}}
							accept="application/pdf,.pdf"
							maxSizeMB={5}
							description="Max 5MB (PDF format only)"
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NMPStep6;
